import { Box, debounce, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAccessPointsList } from '../../api/mobileapi';
import { SearchAccessPointData } from '../../api/types';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { FIRST_PAGE_INDEX } from '../../utils/utils';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { useCustomNavigation } from '../NavigationProvider/NavigationProvider';
import { Severity, useToast } from '../ToastProvider/ToastProvider';
import { useTranslations } from '../Translations/TranslationsProvider';
import { AccessPointsResults } from './util/AccessPointsResults';
import { ScanAccessPoint } from './util/ScanAccessPoint';

export const ListAccessPoints: React.FC = () => {
    const location = useLocation();
    const toast = useToast();

    const { isRefresh } = useCustomNavigation();
    const { currentLanguage } = useHomeData();
    const { translations } = useTranslations();
    const [accessPoints, setAccessPoints] = useState<SearchAccessPointData[] | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE_INDEX);
    const [currentHint, setCurrentHint] = useState<string>('');
    const [totalResults, setTotalResults] = useState<number>(0);

    const handleSearch = (hint: string, page: number) =>
        fetchAccessPointsList({ hint, language: currentLanguage, page });

    const { mutate: getAccessPointsList, isLoading } = useMutation(
        ({ hint, page }: { hint: string; page: number }) => handleSearch(hint, page),
        {
            onSuccess: (data, variables) => {
                const newData =
                    variables.page === FIRST_PAGE_INDEX
                        ? data.accessPoints
                        : [...(accessPoints ?? []), ...data.accessPoints];
                setAccessPoints(newData);
                setCurrentPage(variables.page + 1);
                setTotalResults(data.totalResults);
            },
            onError: (err: AdamsError) => {
                const message = getErrorMessage(err);
                if (message) toast(message, Severity.error);
            },
        }
    );

    const setValuesOnReturn = () => {
        const queryParams = new URLSearchParams(location.search);
        const hint = queryParams.get('hint');
        if (hint) {
            setCurrentHint(hint);
            getAccessPointsList({ hint, page: FIRST_PAGE_INDEX });
        }
    };

    useEffect(() => {
        if (!isRefresh) {
            setValuesOnReturn();
        }
    }, []);

    const debouncedSearch = useCallback(
        debounce((hint: string) => {
            if (hint.length > 0) {
                getAccessPointsList({ hint, page: FIRST_PAGE_INDEX });
            } else setAccessPoints(null);
        }, 500),
        []
    );

    const search = (hint: string) => {
        setCurrentHint(hint);
        debouncedSearch(hint);
    };

    return (
        <>
            <>
                <Box sx={{ padding: '0.25rem' }}>
                    <Typography variant="h5" gutterBottom sx={{ mb: '2rem' }}>
                        {translations.MENU_LINK_SEARCH_ACCESS_POINT}
                    </Typography>
                    <Box>
                        <FormControl fullWidth>
                            <TextField
                                autoFocus
                                autoComplete="off"
                                label={translations.SEARCH_ACCESS_POINT_HINT}
                                variant="outlined"
                                onChange={(e) => search(e.target.value)}
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                value={currentHint}
                            />
                        </FormControl>
                    </Box>

                    {accessPoints === null && !isLoading && <ScanAccessPoint />}

                    {isLoading && currentPage === FIRST_PAGE_INDEX ? (
                        <LoadingSpinner />
                    ) : (
                        <AccessPointsResults
                            accessPoints={accessPoints}
                            currentHint={currentHint}
                            currentPage={currentPage}
                            getAccessPointsList={getAccessPointsList}
                            isLoading={isLoading}
                            totalResults={totalResults}
                        />
                    )}
                </Box>
            </>
        </>
    );
};
